import React, { useEffect, useState } from "react";

import FileIcon from "@uui/File07";
import { every, map, sumBy, uniq } from "lodash/fp";

import { Box, Collapse, Typography } from "@mui/material";

import PlaylistCover from "@app/analytics/assets/clip_playlist_cover.png";
import {
  StyledVideo,
  VideoBackdrop,
  VideoContainer,
  VideoControlsContainer,
  VideoInnerContainer,
} from "@app/analytics/components/ExpandableVideo/styles";
import PlaybackOverlay, {
  PlaybackOverlayButton,
} from "@app/analytics/components/PlaybackOverlay";
import PlayClip from "@app/analytics/components/PlayClip";
import PlayerControls from "@app/analytics/components/PlayerControls";
import useHlsVideo from "@app/analytics/hooks/useHlsVideo";
import useVideoClip from "@app/analytics/hooks/useVideoClip";
import { SecondaryTextWithIcon } from "@app/analytics/Pulse/components/styles";
import type { PulseTopicClip } from "@app/analytics/Pulse/types";
import { getFormattedDuration } from "@app/analytics/utils";
import usePagination from "@app/common/hooks/usePagination";
import type {
  VideoRecording,
  UnauthorizedVideoRecording,
} from "@app/common/types";
import { pluralizePhrase } from "@app/common/utils";

import { Column, Row } from "@rui/Stack";

import { ClipDetails, ClipWrapper, Thumbnail, UpNext } from "./styles";

interface ClipPlaylistProps {
  clips: PulseTopicClip[];
  recordingInfo: Map<
    string,
    {
      isLoading: boolean;
      recordingInfo?: VideoRecording | UnauthorizedVideoRecording;
    }
  >;
}

const MAX_COUNTDOWN_SECONDS = 3;

const VideoClip = ({
  clip,
  onClose,
  onPrevious,
  onNext,
  recordingInfo,
  upNext,
}: {
  clip: PulseTopicClip;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  recordingInfo: VideoRecording;
  upNext?: PulseTopicClip;
}) => {
  const [autoProgress, setAutoProgress] = useState(true);
  const [secondsToNext, setSecondsToNext] = useState<number | null>(null);

  const { video, videoRef } = useHlsVideo(recordingInfo, {
    startTimestamp: clip.start,
  });

  const {
    currentTime,
    controls,
    onActionClick,
    onScrubberChange,
    onVideoClick,
    played,
  } = useVideoClip(video, clip, { autoPlay: true });

  const startCountdown = played && Boolean(onNext) && autoProgress;

  useEffect(() => {
    // reset the auto-progress for each clip
    setAutoProgress(true);
  }, [clip]);

  useEffect(() => {
    if (!startCountdown) {
      setSecondsToNext(null);
      return;
    }

    setSecondsToNext(MAX_COUNTDOWN_SECONDS);

    const interval = window.setInterval(
      () =>
        setSecondsToNext((sec) =>
          sec == null ? MAX_COUNTDOWN_SECONDS : sec - 1
        ),
      1000
    );

    return () => clearInterval(interval);
  }, [startCountdown]);

  useEffect(() => {
    if ((secondsToNext ?? Infinity) < 1) {
      onNext?.();
      setSecondsToNext(null);
    }
  }, [secondsToNext]);

  const handleClose = () => {
    controls.pause();
    controls.jumpToTimestamp(clip.start);
    onClose();
  };

  const handleBackdropClick: React.MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();

    handleClose();
  };

  const handleActionClick: typeof onActionClick = (action) => {
    switch (action) {
      case "previous":
        return onPrevious?.();
      case "next":
        return onNext?.();
      case "minimize":
        return handleClose();
      default:
        onActionClick(action);
    }
  };

  const handleCancelAutoProgression = () => {
    setSecondsToNext(null);
    setAutoProgress(false);
  };

  const upNextToShow = upNext != null && played ? upNext : null;

  return (
    <>
      <VideoBackdrop onClick={handleBackdropClick} />
      <VideoInnerContainer expanded onClick={onVideoClick}>
        <VideoContainer>
          <StyledVideo
            id={`playlist-clip-${clip.sessionId}-${clip.start}`}
            ref={videoRef}
            preload="auto"
            autoPlay
          />
          {controls.paused ? (
            <PlaybackOverlay>
              <Column spacing={3} variant="center">
                {upNextToShow != null && (
                  <Column variant="center" spacing={1} maxWidth="300px">
                    <UpNext spacing={0.5}>
                      <Typography
                        variant="labelMed"
                        fontWeight={600}
                        textTransform="uppercase"
                      >
                        Up next
                      </Typography>
                      <Typography variant="bodySecondaryMed">
                        {upNextToShow.meetingTitle} (
                        {getFormattedDuration(
                          Math.floor(upNextToShow.duration),
                          { unit: "seconds" }
                        )}
                        )
                      </Typography>
                    </UpNext>
                    <Collapse
                      in={autoProgress && secondsToNext != null}
                      unmountOnExit
                    >
                      <PlaybackOverlayButton
                        icon="pause"
                        loadDuration={MAX_COUNTDOWN_SECONDS}
                        onClick={handleCancelAutoProgression}
                      />
                    </Collapse>
                  </Column>
                )}
                <Row spacing={3} variant="center">
                  <PlaybackOverlayButton
                    icon="previous"
                    onClick={onPrevious}
                    disabled={!onPrevious}
                  />
                  <PlaybackOverlayButton
                    icon={played ? "replay" : "play"}
                    onClick={onVideoClick}
                  />
                  <PlaybackOverlayButton
                    icon="next"
                    onClick={onNext}
                    disabled={!onNext}
                  />
                </Row>
              </Column>
            </PlaybackOverlay>
          ) : (
            <VideoControlsContainer>
              <PlayerControls
                additionalActions={[
                  { action: "previous", disabled: !onPrevious },
                  { action: "next", disabled: !onNext },
                ]}
                currentTime={currentTime}
                duration={clip.duration}
                muted={controls.muted}
                onActionClick={handleActionClick}
                onScrubberChange={onScrubberChange}
                paused={controls.paused}
                expandable
                expanded
              />
            </VideoControlsContainer>
          )}
        </VideoContainer>
      </VideoInnerContainer>
    </>
  );
};

const ClipPlaylist = ({ clips, recordingInfo }: ClipPlaylistProps) => {
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const {
    currentPage,
    goToNextPage,
    goToPage,
    goToPreviousPage,
    hasPrevious,
    hasNext,
  } = usePagination(clips, { pageSize: 1 });

  const showPlaylistCover = every(
    (ri) => !ri.isLoading,
    Object.values(Object.fromEntries(recordingInfo.entries()))
  );

  if (!showPlaylistCover) return null;

  const sessionIds = uniq(map("sessionId", clips));
  const totalDuration = sumBy("duration", clips);

  const handleOpenPlaylist = () => setPlaylistOpen(true);
  const handleClosePlaylist = () => {
    goToPage(0);
    setPlaylistOpen(false);
  };

  const activeClip = clips[currentPage];

  return (
    <>
      <Column sx={{ pl: 3 }}>
        <ClipWrapper>
          <Column
            onClick={handleOpenPlaylist}
            sx={{ position: "relative", "&:hover": { cursor: "pointer" } }}
          >
            <Thumbnail
              sx={{ background: (theme) => theme.palette.gradients.light }}
              variant="center"
            >
              <Box component="img" src={PlaylistCover} width="128px" />
            </Thumbnail>
            <PlayClip
              duration={Math.floor(totalDuration)}
              sx={{ position: "absolute", bottom: "8px", right: "8px" }}
            />
          </Column>
          <ClipDetails spacing={0.5}>
            <Column variant="left" spacing={0.5}>
              <Typography
                variant="bodySecondaryReg"
                color="shades.neutral.600"
                sx={{
                  fontFamily: (theme) => theme.typography.fontFamilySecondary,
                }}
              >
                Watch all clips ({clips.length})
              </Typography>
              <SecondaryTextWithIcon
                Icon={FileIcon}
                sx={{ fontSize: "10px", fontWeight: 400 }}
              >
                {pluralizePhrase(sessionIds.length, "meeting")}
              </SecondaryTextWithIcon>
            </Column>
          </ClipDetails>
        </ClipWrapper>
      </Column>
      {playlistOpen && (
        <VideoClip
          clip={activeClip}
          onClose={handleClosePlaylist}
          onNext={hasNext ? goToNextPage : undefined}
          onPrevious={hasPrevious ? goToPreviousPage : undefined}
          recordingInfo={
            recordingInfo.get(activeClip.sessionId)
              ?.recordingInfo as VideoRecording
          }
          upNext={clips[currentPage + 1]}
        />
      )}
    </>
  );
};

export default ClipPlaylist;
